import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useParams, Link, useNavigate, useSearch } from '@tanstack/react-router';
import client from '@/api/client';
import type { Hostel, Tenant, PageResponse, TenantStatus } from '@/api/types';
import { UserCheck, Building2, Phone, Mail, BedDouble, LogIn, CheckCircle2, Clock, Download } from 'lucide-react';
import { TenantExportDialog } from '@/components/TenantExportDialog';
import { SignTermsOnBehalfInline } from '@/components/SignTermsOnBehalfInline';
import { Select } from '@/components/ui/Select';
import { Pagination } from '@/components/ui/Pagination';
import { usePagination } from '@/hooks/usePagination';

const STATUS_COLORS: Record<string, string> = {
  ACTIVE:      'bg-green-100 text-green-800',
  RESERVED:    'bg-amber-100 text-amber-800',
  CHECKED_OUT: 'bg-gray-100 text-gray-600',
  CANCELLED:   'bg-red-100 text-red-700',
};

function CheckInInline({ tenantId, onDone }: { tenantId: string; onDone: () => void }) {
  const { t } = useTranslation();
  const [joiningDate, setJoiningDate] = useState(new Date().toISOString().split('T')[0]);
  const mutation = useMutation({
    mutationFn: () => client.post(`/api/tenants/${tenantId}/checkin`, { joiningDate }),
    onSuccess: onDone,
  });
  return (
    <div className="mt-3 bg-teal-50 border border-teal-200 rounded-xl p-3 flex flex-wrap items-end gap-3"
      onClick={e => e.stopPropagation()}>
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">{t('owner.actualJoiningDate', 'Actual Joining Date')}</label>
        <input type="date" value={joiningDate} onChange={e => setJoiningDate(e.target.value)}
          className="border rounded-lg px-3 py-1.5 text-sm" />
      </div>
      <button onClick={() => mutation.mutate()} disabled={mutation.isPending}
        className="bg-teal-600 hover:bg-teal-700 text-white px-4 py-1.5 rounded-lg text-sm font-medium disabled:opacity-50">
        {mutation.isPending ? t('owner.checkingIn', 'Checking In…') : t('owner.confirmCheckIn', 'Confirm Check-In')}
      </button>
      {mutation.isError && <p className="w-full text-red-600 text-xs">{(mutation.error as Error).message}</p>}
    </div>
  );
}

export function OwnerTenantsPage() {
  const { t } = useTranslation();
  const { id: hostelId } = useParams({ from: '/protected/owner/hostels/$id/tenants' });
  const navigate = useNavigate();
  const urlSearch = useSearch({ from: '/protected/owner/hostels/$id/tenants' });
  const qc = useQueryClient();

  const status = urlSearch.status as TenantStatus | '';
  const [searchText, setSearchText] = useState('');
  const [checkinOpen, setCheckinOpen] = useState<string | null>(null);
  const [signOpen, setSignOpen] = useState<string | null>(null);
  const [exportOpen, setExportOpen] = useState(false);
  const { page, size, setPage, resetPage } = usePagination();

  // Fetch all hostels so owner can switch between them
  const { data: hostels = [] } = useQuery<Hostel[]>({
    queryKey: ['my-hostels'],
    queryFn: () => client.get('/api/hostels/my').then(r => r as unknown as Hostel[]),
  });
  const activeHostels = hostels.filter(h => h.status === 'ACTIVE');

  const { data, isLoading, isError, error } = useQuery<PageResponse<Tenant>>({
    queryKey: ['tenants', hostelId, status, searchText, page],
    queryFn: () => {
      const params = new URLSearchParams({ hostelId, page: String(page), size: String(size) });
      if (status) params.set('status', status);
      if (searchText) params.set('search', searchText);
      return client.get(`/api/tenants?${params}`).then(r => r as unknown as PageResponse<Tenant>);
    },
  });

  const tenants = data?.content ?? [];
  const currentHostel = hostels.find(h => h.id === hostelId);

  function goToTenant(tenantId: string) {
    void navigate({
      to: '/owner/hostels/$id/tenants/$tenantId',
      params: { id: hostelId, tenantId },
      search: { tab: 'profile' },
    });
  }

  function changeHostel(newHostelId: string) {
    resetPage();
    void navigate({
      to: '/owner/hostels/$id/tenants',
      params: { id: newHostelId },
      search: { status },
    });
  }

  function changeStatus(val: string) {
    resetPage();
    void navigate({
      to: '/owner/hostels/$id/tenants',
      params: { id: hostelId },
      search: { status: val },
    });
  }

  const STATUS_FILTERS: { value: TenantStatus | ''; label: string }[] = [
    { value: 'ACTIVE',      label: t('common.active') },
    { value: 'RESERVED',    label: t('common.reserved') },
    { value: 'CHECKED_OUT', label: t('common.checkedOut') },
    { value: 'CANCELLED',   label: t('common.cancelled') },
    { value: '',            label: t('common.all') },
  ];

  return (
    <div>
      {/* Header */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <Link to="/owner/hostels/$id" params={{ id: hostelId }} search={{ bedStatus: '' }} className="text-teal-600 text-sm hover:underline">
          ← {t('owner.hostel', 'Hostel')}
        </Link>
        <span className="text-gray-300">/</span>
        <h1 className="text-xl font-bold text-gray-900">{t('tenant.title')}</h1>
        <button
          onClick={() => setExportOpen(true)}
          className="ml-auto flex items-center gap-1.5 border border-teal-300 text-teal-700 hover:bg-teal-50 px-3 py-1.5 rounded-lg text-xs font-medium transition"
        >
          <Download size={13} /> {t('export.button', 'Export')}
        </button>
      </div>

      {exportOpen && (
        <TenantExportDialog
          hostelId={hostelId}
          status={status}
          accent="teal"
          onClose={() => setExportOpen(false)}
        />
      )}

      {/* Hostel filter + search */}
      <div className="flex flex-wrap gap-2 mb-4">
        {activeHostels.length > 1 && (
          <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-lg px-3 py-1.5 shadow-sm">
            <Building2 size={14} className="text-teal-600 shrink-0" />
            <Select
              value={hostelId}
              onChange={e => changeHostel(e.target.value)}
              options={activeHostels.map(h => ({ value: h.id, label: h.name }))}
              className="w-auto border-0 shadow-none bg-transparent px-0 py-0 pr-6 font-medium text-gray-700"
            />
          </div>
        )}
        {activeHostels.length === 1 && currentHostel && (
          <div className="flex items-center gap-2 bg-white border border-gray-100 rounded-lg px-3 py-1.5 shadow-sm">
            <Building2 size={14} className="text-teal-600" />
            <span className="text-sm font-medium text-gray-700">{currentHostel.name}</span>
          </div>
        )}
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">{t('common.search', 'Search')}</label>
          <input
            value={searchText}
            onChange={e => setSearchText(e.target.value)}
            placeholder={t('owner.searchNamePhone', 'Search name / phone…')}
            className="h-9 border border-gray-300 rounded-lg px-3 text-sm w-44"
          />
        </div>
      </div>

      {/* Status filter chips */}
      <div className="flex gap-1.5 mb-5 flex-wrap">
        {STATUS_FILTERS.map(f => (
          <button
            key={f.value}
            onClick={() => changeStatus(f.value)}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition ${
              status === f.value
                ? 'bg-teal-600 text-white shadow-sm'
                : 'bg-white border border-gray-200 text-gray-600 hover:border-teal-300'
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* Error */}
      {isError && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-4 text-red-700 text-sm">
          {t('owner.failedToLoadTenants', 'Failed to load tenants')}: {(error as Error)?.message ?? t('owner.unknownError', 'Unknown error')}
        </div>
      )}

      {/* Loading */}
      {isLoading ? (
        <div className="bg-white rounded-xl p-10 text-center border border-gray-100">
          <div className="w-8 h-8 border-2 border-teal-500 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
          <p className="text-gray-400 text-sm">{t('owner.loadingTenants', 'Loading tenants…')}</p>
        </div>

      /* Empty */
      ) : tenants.length === 0 ? (
        <div className="bg-white rounded-xl p-10 text-center border border-dashed border-gray-200">
          <UserCheck size={36} className="text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500 font-medium">{t('owner.noTenantsFound', 'No tenants found')}</p>
          {status === 'ACTIVE' && (
            <p className="text-xs text-gray-400 mt-1">{t('owner.tenantsAppearHere', 'Tenants appear here once admitted to a bed.')}</p>
          )}
        </div>

      /* Card grid */
      ) : (
        <>
          <p className="text-xs text-gray-400 mb-3">{t('owner.tenantCount', '{{count}} tenants', { count: tenants.length })}</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-3">
            {tenants.map(tenant => (
              <div key={tenant.id} className="bg-white rounded-xl shadow-sm border border-gray-100 hover:border-teal-300 hover:shadow-md transition flex flex-col">
                <button
                  onClick={() => goToTenant(tenant.id)}
                  className="flex-1 p-4 text-left flex flex-col items-center text-center"
                >
                  {/* Avatar */}
                  <div className="w-14 h-14 rounded-full bg-teal-100 flex items-center justify-center text-teal-700 font-bold text-2xl mb-3 overflow-hidden shrink-0">
                    {tenant.photoUrl
                      ? <img src={tenant.photoUrl} alt={tenant.fullName} className="w-full h-full object-cover" />
                      : tenant.fullName.charAt(0).toUpperCase()}
                  </div>

                  {/* Name + status */}
                  <p className="font-semibold text-gray-900 leading-tight">{tenant.fullName}</p>
                  <span className={`mt-1 px-2 py-0.5 rounded-full text-[10px] font-medium ${STATUS_COLORS[tenant.status] ?? 'bg-gray-100 text-gray-600'}`}>
                    {tenant.status.replace('_', ' ')}
                  </span>

                  {/* Room / bed */}
                  {tenant.roomNumber && (
                    <div className="flex items-center gap-1 mt-2 text-xs text-teal-600 font-medium">
                      <BedDouble size={11} />
                      {t('common.room')} {tenant.roomNumber}{tenant.bedLabel ? ` · ${tenant.bedLabel}` : ''}
                    </div>
                  )}

                  {/* Contact */}
                  <div className="mt-2 space-y-0.5 w-full">
                    <div className="flex items-center gap-1 justify-center text-[11px] text-gray-500">
                      <Phone size={10} /> {tenant.phone}
                    </div>
                    {tenant.email && (
                      <div className="flex items-center gap-1 justify-center text-[11px] text-gray-400 truncate">
                        <Mail size={10} /> {tenant.email}
                      </div>
                    )}
                  </div>

                  {/* Agreement status */}
                  <div className={`mt-2.5 flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium ${
                    tenant.termsAccepted
                      ? 'bg-green-50 text-green-700'
                      : 'bg-amber-50 text-amber-700'
                  }`}>
                    {tenant.termsAccepted
                      ? <><CheckCircle2 size={10} /> {t('tenant.agreementSigned')}</>
                      : <><Clock size={10} /> {t('tenant.agreementPending')}</>
                    }
                  </div>

                  {/* Rent */}
                  <p className="mt-3 text-sm font-bold text-teal-700">₹{Number(tenant.monthlyRent).toLocaleString()}<span className="text-[10px] font-normal text-gray-400">/mo</span></p>
                  <p className="text-[10px] text-gray-400 mt-0.5">{t('owner.joinedOn', 'Joined {{date}}', { date: tenant.joiningDate })}</p>
                </button>

                {/* Check-in button for reserved tenants */}
                {tenant.status === 'RESERVED' && (
                  <div className="px-4 pb-4">
                    <button
                      onClick={e => { e.stopPropagation(); setCheckinOpen(checkinOpen === tenant.id ? null : tenant.id); }}
                      className="w-full flex items-center justify-center gap-1.5 bg-amber-500 hover:bg-amber-600 text-white px-3 py-1.5 rounded-lg text-xs font-medium transition"
                    >
                      <LogIn size={12} /> {t('tenant.checkIn')}
                    </button>
                    {checkinOpen === tenant.id && (
                      <CheckInInline
                        tenantId={tenant.id}
                        onDone={() => {
                          setCheckinOpen(null);
                          void qc.invalidateQueries({ queryKey: ['tenants'] });
                          void qc.invalidateQueries({ queryKey: ['owner-dashboard'] });
                        }}
                      />
                    )}
                  </div>
                )}

                {/* Sign terms on behalf, for tenants who haven't signed yet */}
                {!tenant.termsAccepted && tenant.status !== 'CHECKED_OUT' && tenant.status !== 'CANCELLED' && (
                  <div className="px-4 pb-4" onClick={e => e.stopPropagation()}>
                    <button
                      onClick={() => setSignOpen(signOpen === tenant.id ? null : tenant.id)}
                      className="w-full flex items-center justify-center gap-1.5 border border-amber-300 text-amber-700 hover:bg-amber-50 px-3 py-1.5 rounded-lg text-xs font-medium transition"
                    >
                      <Clock size={12} /> {t('tenant.signOnBehalfAction', 'Sign Terms')}
                    </button>
                    {signOpen === tenant.id && (
                      <div className="mt-3">
                        <SignTermsOnBehalfInline
                          tenantId={tenant.id}
                          hostel={currentHostel ?? null}
                          onDone={() => {
                            setSignOpen(null);
                            void qc.invalidateQueries({ queryKey: ['tenants'] });
                          }}
                        />
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
          <Pagination
            page={page}
            totalPages={data?.totalPages ?? 0}
            totalElements={data?.totalElements ?? 0}
            size={size}
            onPageChange={setPage}
            className="px-1 pb-3"
          />
        </>
      )}
    </div>
  );
}
